/**
 * Recorded-measurements list — mirrors the mobile `MeasurementsCard`
 * for surface parity. Each ScaleAnchor renders with a human-readable
 * summary so the customer can see what's been captured without
 * needing the iPhone.
 *
 * Admins also see this on the review screen so they have a clear
 * audit of every measurement before approving.
 */

import { useState } from "react";

import { deleteAnchor } from "@starz/shared/api/sdk.gen";
import type { ScaleAnchor } from "@starz/shared/api/types.gen";

interface Props {
  anchors: ScaleAnchor[];
  /** When true (the estimate is still `in_progress` and we're not in
   * admin-read mode), each row gets a delete button. The handler
   * lifts the deleted anchor up so the parent refetches. */
  estimateId?: string;
  canDelete?: boolean;
  onDeleted?: (anchorId: string) => void;
}

function formatRoomPlan(a: ScaleAnchor): string | null {
  const rp = a.room_plan_payload;
  if (!rp) return null;
  return `floor ${rp.floor_sqft.toFixed(0)} sqft · walls ${rp.wall_sqft.toFixed(
    0,
  )} sqft · linear ${rp.linear_ft.toFixed(0)} ft · ceiling ${(
    rp.ceiling_height_inches / 12
  ).toFixed(1)} ft`;
}

function formatLinear(a: ScaleAnchor): string | null {
  const lin = a.ar_linear_payload;
  if (!lin) return null;
  return `${(lin.length_inches / 12).toFixed(1)} ft (${lin.length_inches.toFixed(0)} in)`;
}

function formatSurface(a: ScaleAnchor): string | null {
  const surf = a.ar_surface_payload;
  if (!surf) return null;
  return `${surf.area_sqft.toFixed(1)} sqft · ${surf.polygon_points.length} corners`;
}

function formatManual(a: ScaleAnchor): string | null {
  if (a.value_inches == null) return null;
  return `${(a.value_inches / 12).toFixed(1)} ft (${a.value_inches.toFixed(0)} in)`;
}

interface Row {
  primary: string;
  secondary: string | null;
}

function describeAnchor(a: ScaleAnchor): Row {
  if (a.kind === "room_plan") {
    return { primary: "iPhone scan", secondary: formatRoomPlan(a) };
  }
  if (a.kind === "ar_linear") {
    const lidar = a.ar_linear_payload?.device_has_lidar === false ? " (approximate)" : "";
    return { primary: `iPhone measurement${lidar}`, secondary: formatLinear(a) };
  }
  if (a.kind === "ar_surface") {
    const lidar = a.ar_surface_payload?.device_has_lidar === false ? " (approximate)" : "";
    return { primary: `iPhone surface${lidar}`, secondary: formatSurface(a) };
  }
  // Manual / known_dimensions / wall_height anchors carry value_text.
  return {
    primary: a.value_text ? `"${a.value_text}"` : a.kind.replace("_", " "),
    secondary: formatManual(a),
  };
}

export function MeasurementsList({
  anchors,
  estimateId,
  canDelete = false,
  onDeleted,
}: Props) {
  const [removing, setRemoving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function onRemove(anchorId: string) {
    if (!estimateId || !canDelete) return;
    if (
      !window.confirm(
        "Delete this measurement? This can't be undone — Stella will stop using it for the estimate.",
      )
    ) {
      return;
    }
    setRemoving(anchorId);
    setError(null);
    const result = await deleteAnchor({ estimateId, anchorId });
    setRemoving(null);
    if ("error" in result) {
      setError(result.error.error.message);
      return;
    }
    onDeleted?.(anchorId);
  }

  if (anchors.length === 0) {
    return (
      <div className="rounded-xl border border-starz-gold/15 bg-starz-cream/40 p-5">
        <h3 className="font-serif text-xl text-starz-navy">Recorded measurements</h3>
        <p className="mt-2 text-sm text-warm-gray">
          No measurements yet. Capture one from the mobile app, or tell Stella a wall length /
          ceiling height in chat.
        </p>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-starz-gold/15 bg-white p-5 shadow-sm">
      <h3 className="font-serif text-xl text-starz-navy">Recorded measurements</h3>
      <p className="mt-1 text-xs uppercase tracking-widest text-warm-gray">
        {anchors.length} captured
      </p>
      {error ? (
        <p className="mt-2 border-l-2 border-starz-brick pl-3 text-xs text-starz-brick">
          {error}
        </p>
      ) : null}
      <ul className="mt-3 space-y-2">
        {anchors.map((a) => {
          const row = describeAnchor(a);
          return (
            <li
              key={a.anchor_id}
              className="flex items-start justify-between gap-3 rounded-lg border border-starz-gold/10 bg-starz-cream/40 px-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-starz-charcoal">{row.primary}</div>
                {row.secondary ? (
                  <div className="text-xs text-warm-gray">{row.secondary}</div>
                ) : null}
                <div className="mt-1 text-[10px] uppercase tracking-wider text-warm-gray/70">
                  {new Date(a.created_at).toLocaleString()}
                </div>
              </div>
              {canDelete ? (
                <button
                  type="button"
                  onClick={() => void onRemove(a.anchor_id)}
                  disabled={removing === a.anchor_id}
                  aria-label="Delete this measurement"
                  className="shrink-0 rounded-md border border-rose-200 px-2 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-40"
                >
                  {removing === a.anchor_id ? "…" : "Delete"}
                </button>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
